import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BatteryCharging, LucideAngularModule, Pencil, Plus, Trash2, X } from 'lucide-angular';
import { BatteryConfiguration, BessSocLog } from '../../../core/models/domain.models';
import { ToastService } from '../../../shared/ui/toast.service';
import { BatteryConfigurationsService } from '../services/battery-configurations.service';
import { BessOperationsService } from '../services/bess-operations.service';

type SocForm = {
  batteryConfigurationId: string;
  logDate: string;
  timeBlock: string;
  socBefore: string;
  chargingEnergyMw: string;
  dischargingEnergyMw: string;
  socAfter: string;
  status: string;
};

@Component({
  standalone: true,
  selector: 'app-soc-monitoring-page',
  imports: [CommonModule, FormsModule, LucideAngularModule],
  template: `
    <div class="module-shell p-2 p-lg-2">
      <section class="hero-card">
        <div>
          <div class="eyebrow">Bess</div>
          <h1>SOC Monitoring</h1>
          <p>Track state of charge block-wise with stored SOC logs and validation against operating windows.</p>
        </div>
        <div class="hero-actions">
          <div class="hero-icon"><lucide-icon [img]="BatteryChargingIcon" [size]="28"></lucide-icon></div>
          <button type="button" class="primary-btn" (click)="openAdd()">
            <lucide-icon [img]="PlusIcon" [size]="16"></lucide-icon>
            <span>Add Log</span>
          </button>
        </div>
      </section>

      <section class="summary-grid">
        <article class="summary-card"><small>Total Logs</small><strong>{{ rows().length }}</strong></article>
        <article class="summary-card"><small>Ready</small><strong>{{ statusCount('ready') }}</strong></article>
        <article class="summary-card"><small>Alert</small><strong>{{ statusCount('alert') }}</strong></article>
        <article class="summary-card"><small>Assets</small><strong>{{ assets().length }}</strong></article>
      </section>

      <section class="table-card">
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Battery</th>
                <th>Date</th>
                <th>Block</th>
                <th>SOC Before</th>
                <th>Charge MW</th>
                <th>Discharge MW</th>
                <th>SOC After</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              @if (loading()) {
                <tr><td colspan="9" class="empty-state">Loading SOC logs...</td></tr>
              } @else if (rows().length === 0) {
                <tr><td colspan="9" class="empty-state">No SOC logs found.</td></tr>
              } @else {
                @for (row of rows(); track row.id) {
                  <tr>
                    <td>{{ assetLabel(row.battery_configuration_id) }}</td>
                    <td>{{ row.log_date }}</td>
                    <td>{{ row.time_block }}</td>
                    <td>{{ row.soc_before }}%</td>
                    <td>{{ row.charging_energy_mw || '-' }}</td>
                    <td>{{ row.discharging_energy_mw || '-' }}</td>
                    <td>{{ row.soc_after }}%</td>
                    <td><span class="status-pill" [class.alert]="row.status === 'alert'">{{ row.status }}</span></td>
                    <td>
                      <div class="action-list">
                        <button class="icon-btn edit" type="button" (click)="openEdit(row)"><lucide-icon [img]="PencilIcon" [size]="14"></lucide-icon></button>
                        <button class="icon-btn delete" type="button" (click)="remove(row)"><lucide-icon [img]="Trash2Icon" [size]="14"></lucide-icon></button>
                      </div>
                    </td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>
      </section>

      @if (showForm()) {
        <div class="modal-backdrop">
          <div class="modal-card">
            <div class="modal-head">
              <h2>{{ editingId() ? 'Edit SOC Log' : 'Add SOC Log' }}</h2>
              <button type="button" class="close-btn" (click)="closeForm()"><lucide-icon [img]="XIcon" [size]="16"></lucide-icon></button>
            </div>
            <div class="modal-body">
              <div class="form-grid">
                <label class="field-wrap">
                  <span>Battery Asset</span>
                  <select class="form-select" [value]="form().batteryConfigurationId" (change)="setField('batteryConfigurationId', ($any($event.target).value || '').toString())">
                    <option value="">Select asset</option>
                    @for (asset of assets(); track asset.id) {
                      <option [value]="asset.id">{{ asset.entry_id }}</option>
                    }
                  </select>
                </label>
                <label class="field-wrap"><span>Log Date</span><input class="form-control" type="date" [value]="form().logDate" (input)="setField('logDate', ($any($event.target).value || '').toString())" /></label>
                <label class="field-wrap"><span>Time Block</span><input class="form-control" type="number" min="1" max="96" [value]="form().timeBlock" (input)="setField('timeBlock', ($any($event.target).value || '').toString())" /></label>
                <label class="field-wrap"><span>SOC Before</span><input class="form-control" [value]="form().socBefore" (input)="setField('socBefore', ($any($event.target).value || '').toString())" /></label>
                <label class="field-wrap"><span>Charging Energy MW</span><input class="form-control" [value]="form().chargingEnergyMw" (input)="setField('chargingEnergyMw', ($any($event.target).value || '').toString())" /></label>
                <label class="field-wrap"><span>Discharging Energy MW</span><input class="form-control" [value]="form().dischargingEnergyMw" (input)="setField('dischargingEnergyMw', ($any($event.target).value || '').toString())" /></label>
                <label class="field-wrap"><span>SOC After</span><input class="form-control" [value]="form().socAfter" (input)="setField('socAfter', ($any($event.target).value || '').toString())" /></label>
                <label class="field-wrap">
                  <span>Status</span>
                  <select class="form-select" [value]="form().status" (change)="setField('status', ($any($event.target).value || '').toString())">
                    <option value="ready">ready</option>
                    <option value="alert">alert</option>
                    <option value="review">review</option>
                  </select>
                </label>
              </div>
              @if (formError()) { <div class="text-danger small mt-3">{{ formError() }}</div> }
              <div class="modal-actions">
                <button class="primary-btn" type="button" [disabled]="submitting()" (click)="save()">{{ submitting() ? 'Saving...' : 'Save' }}</button>
                <button class="secondary-btn" type="button" (click)="closeForm()">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    .module-shell { display:grid; gap:1rem; }
    .hero-card,.summary-card,.table-card,.modal-card{border:1px solid rgba(18,40,72,.08); border-radius:1rem; background:linear-gradient(180deg,#fff 0%,#f7fafc 100%); box-shadow:0 16px 35px rgba(15,23,42,.06);}
    .hero-card{display:flex; justify-content:space-between; align-items:center; gap:1rem; padding:1.5rem;}
    .eyebrow{text-transform:uppercase; letter-spacing:.08em; font-size:.72rem; color:#6b7b8c; margin-bottom:.35rem;}
    .hero-card h1{margin:0;} .hero-card p,.summary-card small,.empty-state{color:#607286;}
    .hero-actions{display:flex; gap:.75rem; align-items:center;} .hero-icon{width:4rem;height:4rem;display:grid;place-items:center;border-radius:1rem;background:linear-gradient(135deg,#0f766e,#0f4c81);color:#fff;}
    .summary-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:1rem;} .summary-card{padding:1rem 1.1rem;} .summary-card small{display:block;text-transform:uppercase;letter-spacing:.05em;font-size:.74rem;} .summary-card strong{font-size:1.65rem;color:#18324d;}
    .table-card{padding:1.25rem;} .table-wrap{overflow:auto;} table{width:100%;border-collapse:collapse;} th,td{padding:.85rem .75rem;border-bottom:1px solid rgba(18,40,72,.08);text-align:left;white-space:nowrap;} th{color:#6b7b8c;font-size:.8rem;text-transform:uppercase;letter-spacing:.05em;}
    .status-pill{display:inline-flex;padding:.3rem .65rem;border-radius:999px;background:#dcfce7;color:#166534;font-size:.8rem;font-weight:700;text-transform:capitalize;} .status-pill.alert{background:#fee2e2;color:#b91c1c;}
    .action-list{display:inline-flex;gap:.5rem;} .icon-btn{width:36px;height:36px;border-radius:.7rem;border:0;display:inline-flex;align-items:center;justify-content:center;} .icon-btn.edit{background:#dbeafe;color:#1d4ed8;} .icon-btn.delete{background:#fee2e2;color:#dc2626;}
    .empty-state{text-align:center;padding:2rem .5rem;}
    .modal-backdrop{position:fixed;inset:0;background:rgba(15,23,42,.45);display:flex;align-items:center;justify-content:center;padding:1rem;z-index:1200;}
    .modal-card{width:min(760px,100%);} .modal-head{display:flex;justify-content:space-between;align-items:center;padding:1rem 1.25rem;border-bottom:1px solid rgba(18,40,72,.08);} .modal-head h2{margin:0;} .close-btn{border:0;background:#eef2f7;border-radius:999px;width:34px;height:34px;}
    .modal-body{padding:1.25rem;} .form-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:1rem;} .field-wrap{display:grid;gap:.4rem;} .field-wrap span{font-size:.88rem;color:#526172;font-weight:600;}
    .modal-actions{display:flex;justify-content:center;gap:1rem;margin-top:1.25rem;} .primary-btn,.secondary-btn{border:0;border-radius:.8rem;padding:.8rem 1rem;}
    .primary-btn{background:linear-gradient(135deg,#0f766e,#0f4c81);color:#fff;} .secondary-btn{background:#e8edf3;color:#1f3349;}
    @media (max-width:960px){.summary-grid,.form-grid{grid-template-columns:1fr;} .hero-card{flex-direction:column;align-items:flex-start;}}
  `],
})
export class SocMonitoringPage {
  private readonly batteryService = inject(BatteryConfigurationsService);
  private readonly operationsService = inject(BessOperationsService);
  private readonly toast = inject(ToastService);

  protected readonly BatteryChargingIcon = BatteryCharging;
  protected readonly PlusIcon = Plus;
  protected readonly PencilIcon = Pencil;
  protected readonly Trash2Icon = Trash2;
  protected readonly XIcon = X;
  protected readonly loading = signal(false);
  protected readonly submitting = signal(false);
  protected readonly showForm = signal(false);
  protected readonly editingId = signal<string | null>(null);
  protected readonly formError = signal('');
  protected readonly assets = signal<BatteryConfiguration[]>([]);
  protected readonly rows = signal<BessSocLog[]>([]);
  protected readonly form = signal<SocForm>(this.emptyForm());

  constructor() {
    this.load();
  }

  protected statusCount(status: string): number {
    return this.rows().filter((item) => item.status === status).length;
  }

  protected assetLabel(id: string): string {
    return this.assets().find((item) => item.id === id)?.entry_id || id;
  }

  protected openAdd(): void {
    this.editingId.set(null);
    this.formError.set('');
    this.form.set(this.emptyForm());
    this.showForm.set(true);
  }

  protected openEdit(row: BessSocLog): void {
    this.editingId.set(row.id);
    this.formError.set('');
    this.form.set({
      batteryConfigurationId: row.battery_configuration_id,
      logDate: row.log_date,
      timeBlock: String(row.time_block),
      socBefore: String(row.soc_before),
      chargingEnergyMw: row.charging_energy_mw !== null && row.charging_energy_mw !== undefined ? String(row.charging_energy_mw) : '',
      dischargingEnergyMw: row.discharging_energy_mw !== null && row.discharging_energy_mw !== undefined ? String(row.discharging_energy_mw) : '',
      socAfter: String(row.soc_after),
      status: row.status || 'ready',
    });
    this.showForm.set(true);
  }

  protected closeForm(): void {
    this.showForm.set(false);
    this.editingId.set(null);
  }

  protected setField<K extends keyof SocForm>(key: K, value: SocForm[K]): void {
    this.form.update((current) => ({ ...current, [key]: value }));
  }

  protected save(): void {
    if (this.submitting()) return;
    const raw = this.form();
    if (!raw.batteryConfigurationId || !raw.logDate || !raw.timeBlock || !raw.socBefore || !raw.socAfter) {
      this.formError.set('Battery, date, block, SOC before and SOC after are required.');
      return;
    }
    const payload: Partial<BessSocLog> = {
      battery_configuration_id: raw.batteryConfigurationId,
      log_date: raw.logDate,
      time_block: Number(raw.timeBlock),
      soc_before: Number(raw.socBefore),
      charging_energy_mw: raw.chargingEnergyMw ? Number(raw.chargingEnergyMw) : null,
      discharging_energy_mw: raw.dischargingEnergyMw ? Number(raw.dischargingEnergyMw) : null,
      soc_after: Number(raw.socAfter),
      status: raw.status || 'ready',
    };
    this.submitting.set(true);
    const req$ = this.editingId()
      ? this.operationsService.updateSocLog(this.editingId()!, payload)
      : this.operationsService.createSocLog(payload);
    req$.subscribe({
      next: () => {
        this.submitting.set(false);
        this.closeForm();
        this.toast.show('SOC log saved', '', 'success');
        this.load();
      },
      error: () => {
        this.submitting.set(false);
        this.formError.set('Failed to save SOC log.');
      },
    });
  }

  protected remove(row: BessSocLog): void {
    if (!window.confirm(`Delete SOC log for ${this.assetLabel(row.battery_configuration_id)} block ${row.time_block}?`)) return;
    this.operationsService.deleteSocLog(row.id).subscribe({
      next: () => {
        this.toast.show('SOC log deleted', '', 'success');
        this.load();
      },
      error: () => this.toast.show('Failed to delete SOC log', '', 'danger'),
    });
  }

  private load(): void {
    this.loading.set(true);
    this.batteryService.list().subscribe({
      next: (assets) => {
        this.assets.set(assets);
        this.operationsService.listSocLogs().subscribe({
          next: (rows) => {
            this.rows.set(rows);
            this.loading.set(false);
          },
          error: () => {
            this.rows.set([]);
            this.loading.set(false);
            this.toast.show('Failed to load SOC logs', '', 'danger');
          },
        });
      },
      error: () => {
        this.assets.set([]);
        this.rows.set([]);
        this.loading.set(false);
        this.toast.show('Failed to load battery assets', '', 'danger');
      },
    });
  }

  private emptyForm(): SocForm {
    return {
      batteryConfigurationId: '',
      logDate: '',
      timeBlock: '',
      socBefore: '',
      chargingEnergyMw: '',
      dischargingEnergyMw: '',
      socAfter: '',
      status: 'ready',
    };
  }
}
